import { OktaAuth } from '@okta/okta-auth-js';
import { fetchCall, postCall } from '../../services/api';
import { store } from '../../redux/store';
import { tokenHasExpired } from '../../helpers/auth';

/**
 * Handles fetching mapping data
 * @returns {Promise<any>} promise value with mapping data
 */
export async function fetchMappings(oktaAuth: OktaAuth): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(fetchCall(`${api}/data/mapping/client/provided`));
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401) tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject('Error fetching Mapping data. Please try again.');
    }
  });
}

/**
 * Handles posting changed mapping data
 * @param requestBody {Object}
 * @returns {Promise<any>} promise value with response
 */
export async function postMappings(requestBody: any): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      resolve(postCall(`${api}/data/mapping/client/provided/save`, requestBody));
    } catch (error) {
      console.error(error);
      reject('Error posting Mapping data. Please try again.');
    }
  });
}

/**
 * Handles fetching mapping options
 * @param endpoint string
 * @returns {Promise<any>} promise value with options data
 */
export async function fetchOptions(endpoint: string): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      resolve(fetchCall(`${api}/filter/${endpoint}`));
    } catch (error) {
      console.error(error);
      reject(`Error fetching ${endpoint} data. Please try again.`);
    }
  });
}

/**
 * Handles fetching data sets
 * @returns {Promise<any>} promise value with data sets data
 */
export async function fetchDataSets(oktaAuth: OktaAuth): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(fetchCall(`${api}/client/metadata/available/info`));
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401) tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject(`Error fetching data sets. Please try again.`);
    }
  });
}

/**
 * Handles posting Plan and Sales data files
 * @param requestBody {Object}
 * @returns {Promise<any>} promise value with summary data
 */
export async function postUploadData(requestBody: any, oktaAuth: OktaAuth): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(postCall(`${api}/clientdata/upload`, requestBody));
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401) tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject('Error posting Mapping data. Please try again.');
    }
  });
}

/**
 * Handles fetching csv with formatting errors
 * @param fileNameParams any
 * @returns {Promise<any>} promise value with sample file data
 */
export async function fetchErrorsFile(fileNameParams: any): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      resolve(fetchCall(`${api}/clientdata/download`, fileNameParams));
    } catch (error) {
      console.error(error);
      reject(`Error fetching sample file. Please try again.`);
    }
  });
}

export const getSampleFileFromDataSet = (dataset: string): any => {
  let sampleFile = null;
  switch (dataset) {
    case 'Plan Data':
      sampleFile = {
        url: `${process.env.PUBLIC_URL}/samples/Plan_Data_Template.csv`,
        name: 'Plan_Data_Template.csv'
      };
      break;
    case 'Monthly Sales Data':
      sampleFile = {
        url: `${process.env.PUBLIC_URL}/samples/Monthly_Dcomm_Sales_Template.csv`,
        name: 'Monthly_Dcomm_Sales_Template.csv'
      };
      break;
    case 'Detailed Sales Data':
      sampleFile = {
        url: `${process.env.PUBLIC_URL}/samples/Digital_Commerce_Penetration_Data_Template.csv`,
        name: 'Digital_Commerce_Penetration_Data_Template.csv'
      };
      break;

    default:
      break;
  }

  return sampleFile;
};
